import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ReferralTable } from "@/core/components/referral-table/referral-table";
import { Button } from "@volca/ui/components/ui/button";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import { TimeRange } from "@volca/ui/components/ui/time-range-picker";
import { toast } from "@volca/ui/lib/toast";
import {
  SETTINGS_PAGE_TITLES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { ReferralHistoryToolbar } from "@/business-settings/referral-history/referral-history-toolbar";
import { getErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import AddReferralModal from "@/referrals/common/add-referral-modal";
import { PayoutModal } from "@/referrals/common/referral-payout-modal";
import {
  ACTIVE_REFERRAL_STATUSES,
  REFERRAL_FIELD,
} from "@/referrals/constants";
import { REFERRAL_FIELD_TO_SORTER } from "@/referrals/constants";
import { trackReferralUpdate } from "@/referrals/events";
import {
  useGetReferrals,
  useUpdateReferral,
  useDeleteReferral,
} from "@/referrals/query";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";
import { REFERRAL_SEARCH_FIELDS } from "@/referrals/search-constants";

const ReferralHistory = () => {
  const { businessId } = useParams();

  const { data: referrals = [], isLoading: isLoadingReferrals } =
    useGetReferrals(businessId);

  const posthog = usePostHog();
  const {
    state: { selectedItemId, payoutReferral },
    updateSidebar,
  } = useBusinessSettingsSidebar();

  const selectedReferral = useMemo(
    () => referrals.find((r) => r.id === selectedItemId) || null,
    [referrals, selectedItemId],
  );

  const [searchValue, setSearchValue] = useState("");
  const [onlyActiveReferrals, setOnlyActiveReferrals] = useState(false);
  const [addReferralModalOpen, setAddReferralModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);

  const { mutate: updateReferral } = useUpdateReferral(businessId);
  const { mutate: deleteReferral } = useDeleteReferral(businessId);

  const handleUpdateReferral = useCallback(
    (referralId: string, values: Partial<ReferralUpdateParams>) => {
      updateReferral(
        {
          id: referralId,
          updates: values,
        },
        {
          onSuccess: () => {
            if (values.status === "LOST") {
              toast.success("Referral marked as lost");
            } else {
              toast.success("Referral updated successfully");
            }
            const updatedReferral = referrals.find((r) => r.id === referralId);
            if (updatedReferral) {
              trackReferralUpdate(posthog, {
                referral: updatedReferral,
                updates: values,
                method: "sidebar",
                success: true,
              });
            }
          },
          onError: (error) => {
            const updatedReferral = referrals.find((r) => r.id === referralId);
            if (updatedReferral) {
              trackReferralUpdate(posthog, {
                referral: updatedReferral,
                updates: values,
                method: "sidebar",
                success: false,
                error,
              });
            }
            toast.error("Failed to update referral", {
              description: getErrorMessage(error),
            });
          },
        },
      );
    },
    [updateReferral, posthog, referrals],
  );

  const handleDeleteReferral = useCallback(
    (referralId: string) => {
      deleteReferral(
        {
          id: referralId,
        },
        {
          onSuccess: () => {
            toast.success("Referral deleted successfully");
            updateSidebar({
              isOpen: false,
              section: SIDEBAR_SECTIONS.referralHistory,
              mode: SIDEBAR_MODES.view,
              selectedItemId: undefined,
            });
          },
          onError: (error) => {
            toast.error("Failed to delete referral", {
              description: getErrorMessage(error),
            });
          },
        },
      );
    },
    [deleteReferral, updateSidebar],
  );

  const handleRowClick = useCallback(
    (referral: IReferral) => {
      updateSidebar({
        section: SIDEBAR_SECTIONS.referralHistory,
        mode: SIDEBAR_MODES.view,
        selectedItemId: referral.id,
        isOpen: true,
      });
    },
    [updateSidebar],
  );

  const sortedFilteredReferrals = useMemo(() => {
    let filtered = onlyActiveReferrals
      ? referrals.filter((referral) =>
          ACTIVE_REFERRAL_STATUSES.includes(referral.status),
        )
      : [...referrals];

    // Apply search filter using multi-field search
    if (searchValue) {
      filtered = multiFieldSearch(
        filtered,
        searchValue,
        REFERRAL_SEARCH_FIELDS,
      );
    }

    if (timeRange) {
      const from = dayjs(timeRange.from).startOf("day");
      const to = dayjs(timeRange.to).endOf("day");
      filtered = filtered.filter((referral) => {
        const referralDate = dayjs(referral.created_at);
        return (
          referralDate.isSameOrAfter(from) && referralDate.isSameOrBefore(to)
        );
      });
    }

    return filtered.sort(REFERRAL_FIELD_TO_SORTER[REFERRAL_FIELD.status]);
  }, [timeRange, onlyActiveReferrals, referrals, searchValue]);

  const isReferralsEmpty = referrals.length === 0 && !isLoadingReferrals;

  return (
    <>
      <PageContainer variant="workspace">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              {SETTINGS_PAGE_TITLES["referral-history"]}
              {!isReferralsEmpty && (
                <LoadingCount
                  count={sortedFilteredReferrals.length}
                  isCountLoading={isLoadingReferrals}
                  variant="subtle"
                />
              )}
            </span>
          }
          actions={
            <Button
              variant="secondary"
              onClick={() => setAddReferralModalOpen(true)}
            >
              <PlusIcon data-icon="inline-start" />
              Add referral
            </Button>
          }
        />
        <PageMainCard>
          <PageMainStack className="min-h-0 flex-1 pt-6">
            {isReferralsEmpty ? (
              <EmptyTableMessage>No referrals yet.</EmptyTableMessage>
            ) : (
              <>
                <ReferralHistoryToolbar
                  searchValue={searchValue}
                  onSearchChange={setSearchValue}
                  onlyActiveReferrals={onlyActiveReferrals}
                  onOnlyActiveReferralsChange={setOnlyActiveReferrals}
                  timeRange={timeRange}
                  onTimeRangeChange={setTimeRange}
                />
                <ReferralTable
                  referrals={sortedFilteredReferrals}
                  isLoading={isLoadingReferrals}
                  searchValue={searchValue}
                  selectedReferral={selectedReferral}
                  setSearchValue={setSearchValue}
                  onRowClick={handleRowClick}
                  setPayoutReferral={(referral) =>
                    updateSidebar({ payoutReferral: referral })
                  }
                  className="flex-1 pt-0"
                  onLost={(referralId) =>
                    handleUpdateReferral(referralId, { status: "LOST" })
                  }
                  onMoveToPaid={(referralId) =>
                    handleUpdateReferral(referralId, { status: "PAID" })
                  }
                  onDelete={handleDeleteReferral}
                />
              </>
            )}
          </PageMainStack>
        </PageMainCard>
      </PageContainer>
      <PayoutModal
        referral={payoutReferral}
        onOpenChange={(referral) => updateSidebar({ payoutReferral: referral })}
      />
      <AddReferralModal
        businessId={businessId}
        open={addReferralModalOpen}
        onOpenChange={setAddReferralModalOpen}
        onSuccess={(data) => {
          updateSidebar({
            mode: SIDEBAR_MODES.view,
            section: SIDEBAR_SECTIONS.referralHistory,
            selectedItemId: data.id,
            isOpen: true,
          });
        }}
      />
    </>
  );
};

export default ReferralHistory;
